import React from "react";
import { makeStyles } from "tss-react/mui";

export const LOCALE = "es-PE";

const CLOSURE_COLORS = ["#2e7d32", "#1976d2", "#ed6c02", "#8e24aa", "#00897b", "#c2185b", "#5d4037"];
const SIN_CATEGORIA = "__sin_categoria";
const SIN_MOTIVO = "__sin_motivo";

const closureColor = (key, index) => {
  if (key === SIN_CATEGORIA) return "#9e9e9e";
  if (key === SIN_MOTIVO) return "#616161";
  return CLOSURE_COLORS[index % CLOSURE_COLORS.length];
};

export const percent = (part, total) => (total > 0 ? `${Math.round((part / total) * 100)}%` : "—");

export const formatNumber = (v) => Number(v || 0).toLocaleString(LOCALE);

export const formatMoney = (v, moneda) => {
  if (v == null) return null;
  const n = Number(v);
  if (moneda) {
    try {
      const sinDecimales = moneda === "COP";
      return new Intl.NumberFormat(LOCALE, {
        style: "currency",
        currency: moneda,
        minimumFractionDigits: sinDecimales ? 0 : 2,
        maximumFractionDigits: sinDecimales ? 0 : 2,
      }).format(n);
    } catch {
    }
  }
  return n.toLocaleString(LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const closureOptionsOf = (totales = {}) =>
  (totales.cierres || []).map((c, i) => ({ ...c, color: closureColor(c.key, i) }));

export const summaryOf = (totales = {}, platform = "meta") => {
  const variasMonedas = (totales.monedas || []).length > 1;
  const invertido = variasMonedas
    ? "Varias monedas"
    : totales.spend
    ? formatMoney(totales.spend, totales.moneda)
    : "—";

  const porUnidad = (cantidad) =>
    totales.spend && cantidad > 0 ? formatMoney(totales.spend / cantidad, totales.moneda) : "—";

  const kpis =
    platform === "google"
      ? [
          { label: "Invertido", value: invertido },
          {
            label: "Clics",
            value: formatNumber(totales.clicks),
            hint: totales.clicks ? `CPC ${porUnidad(totales.clicks)}` : null,
          },
          {
            label: "Tickets",
            value: totales.tickets ?? 0,
            hint: totales.clicks ? `${percent(totales.tickets || 0, totales.clicks)} de los clics` : null,
          },
          { label: "Costo por ticket", value: porUnidad(totales.tickets || 0) },
        ]
      : [
          { label: "Invertido", value: invertido },
          {
            label: "Conversaciones",
            value: formatNumber(totales.llegadas),
            hint: totales.tickets ? `${totales.tickets} tickets` : null,
          },
          { label: "Costo por conversación", value: porUnidad(totales.llegadas || 0) },
          {
            label: "Cerrados",
            value: totales.cerrados ?? 0,
            hint: totales.tickets ? `${percent(totales.cerrados || 0, totales.tickets)} de los tickets` : null,
          },
        ];

  const cierres = closureOptionsOf(totales);
  const totalCerrados = cierres.reduce((acc, c) => acc + c.total, 0);

  return { kpis, cierres, totalCerrados, porUnidad };
};

const useStyles = makeStyles()((theme) => {
  const border = `1px solid ${theme.palette.divider}`;

  return {
    card: {
      border,
      borderRadius: 12,
      background: theme.palette.background.paper,
      overflow: "hidden",
      flexShrink: 0,
    },
    kpis: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
    },
    kpi: {
      padding: theme.spacing(1.25, 2),
      minWidth: 0,
      "& + &": {
        borderLeft: border,
      },
    },
    kpisCompact: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      "& > div:nth-of-type(odd)": {
        borderLeft: "none",
      },
      "& > div:nth-of-type(n+3)": {
        borderTop: border,
      },
    },
    label: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    kpiValue: {
      fontSize: 21,
      fontWeight: 600,
      lineHeight: 1.25,
      marginTop: 2,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    hint: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      minHeight: 16,
    },
    closuresHead: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      padding: theme.spacing(1, 2, 0),
      borderTop: border,
    },
    closures: {
      display: "grid",
      padding: theme.spacing(0.5, 1, 1),
      columnGap: theme.spacing(1),
      [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr)) !important",
      },
    },
    closure: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      gap: 6,
      padding: theme.spacing(0.75, 1),
      border: "1px solid transparent",
      borderRadius: 8,
      background: "transparent",
      color: theme.palette.text.primary,
      font: "inherit",
      textAlign: "left",
      cursor: "pointer",
      minWidth: 0,
      transition: "background-color .15s, border-color .15s",
      "&:hover:not(:disabled)": {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.divider,
      },
      "&:disabled": {
        cursor: "default",
      },
    },
    closureActive: {
      backgroundColor: theme.palette.action.selected,
      borderColor: theme.palette.divider,
    },
    closureLine: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: theme.spacing(1),
      minWidth: 0,
    },
    closureName: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 13,
      color: theme.palette.text.secondary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    closureValue: {
      display: "flex",
      alignItems: "baseline",
      gap: 6,
      fontSize: 16,
      fontWeight: 600,
      lineHeight: 1.3,
      flexShrink: 0,
    },
    closureShare: {
      fontSize: 12,
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
    },
    track: {
      display: "block",
      height: 3,
      borderRadius: 2,
      backgroundColor: theme.palette.action.hover,
      overflow: "hidden",
      "& > span": {
        display: "block",
        height: "100%",
        borderRadius: 2,
      },
    },
  };
});

const AdsSummary = ({ totales = {}, platform = "meta", activeClosure = null, onClosureClick, compact = false }) => {
  const { classes, cx } = useStyles();
  const { kpis, cierres, totalCerrados, porUnidad } = summaryOf(totales, platform);

  return (
    <section className={classes.card}>
      <div className={cx(classes.kpis, { [classes.kpisCompact]: compact })}>
        {kpis.map((k) => (
          <div key={k.label} className={classes.kpi}>
            <div className={classes.label}>{k.label}</div>
            <div className={classes.kpiValue}>{k.value}</div>
            <div className={classes.hint}>{k.hint || ""}</div>
          </div>
        ))}
      </div>

      <div className={classes.closuresHead}>
        <span className={classes.label}>Cierres por ámbito</span>
        <span className={classes.hint}>
          {totalCerrados
            ? `${totalCerrados} ${totalCerrados === 1 ? "ticket cerrado" : "tickets cerrados"}`
            : "Sin tickets cerrados en este periodo"}
        </span>
      </div>
      <div
        className={classes.closures}
        style={{
          gridTemplateColumns: compact
            ? "repeat(2, minmax(0, 1fr))"
            : `repeat(${Math.max(cierres.length, 1)}, minmax(0, 1fr))`,
        }}
      >
        {cierres.map((c) => {
          const share = totalCerrados ? (c.total / totalCerrados) * 100 : 0;
          const costo = totales.spend && !c.key.startsWith("__") ? ` · ${porUnidad(c.total)} c/u` : "";
          return (
            <button
              key={c.key}
              type="button"
              className={cx(classes.closure, { [classes.closureActive]: activeClosure === c.key })}
              onClick={() => onClosureClick && onClosureClick(c.key)}
              disabled={!c.total || !onClosureClick}
              title={c.total ? `Ver los ${c.total} tickets cerrados como "${c.name}"${costo}` : undefined}
            >
              <span className={classes.closureLine}>
                <span className={classes.closureName}>
                  <span className={classes.dot} style={{ backgroundColor: c.color }} />
                  {c.name}
                </span>
                <span className={classes.closureValue}>
                  {c.total}
                  {c.total > 0 && <span className={classes.closureShare}>{percent(c.total, totalCerrados)}</span>}
                </span>
              </span>
              <span className={classes.track}>
                <span style={{ width: `${share}%`, backgroundColor: c.color }} />
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default AdsSummary;
